import React, { useEffect, useRef } from "react";

/**
 * Hold-timer ring for the face-capture oval.
 *
 * Sits `gap` px outside the oval and is only visible while `active`.
 * While active, progress runs 0 → 1 over `durationMs` (the same clock
 * the detection hook waits before firing the shutter); when `active`
 * drops, the progress unwinds back to 0 over `unwindMs` so the user
 * sees the hold they lost. Progress is written straight to the SVG
 * from a requestAnimationFrame loop, never through React state, so a
 * parent that owns a live webcam isn't re-rendered 60 times a second.
 *
 * Variants:
 *   sweep — one arc fills clockwise from 12 o'clock (the default).
 *   ticks — the ring is a row of short ticks that light up one after
 *           another as the sweep passes them; reads like a scan.
 *   comet — the sweep leaves a dimmer trail and a bright head with a
 *           halo leads it around the oval.
 *
 * On completion (progress reaches 1 while active) the ring gives a
 * single "lock" flash: the stroke widens and fades out, a shutter
 * beat, unless `flashOnComplete` is false.
 *
 * All ellipses that carry the progress are drawn with rx/ry swapped
 * and rotated -90deg about the center: SVG ellipses start their path
 * at 3 o'clock, and the rotation moves that start to the top while
 * keeping the traversal clockwise on screen.
 */
export const HOLD_RING_VARIANTS = ["sweep", "ticks", "comet"];

// Length of the comet head as a share of the ring (pathLength 100).
const HEAD_LEN = 7;
// Ticks variant: dash / gap in pathLength units. 1 + 1.5 = 2.5 → 40 ticks.
const TICK_ON = 1;
const TICK_OFF = 1.5;

let idCounter = 0;

export default function HoldRing({
  cx,
  cy,
  rx,
  ry,
  width,
  height,
  active = false,
  durationMs = 1800,
  unwindMs = 220,
  color = "#1864ab",
  trackColor = "rgba(255,255,255,0.3)",
  thickness = 3.5,
  gap = 10,
  variant = "sweep",
  flashOnComplete = true,
  zIndex = 12,
  style,
}) {
  const sweepRef = useRef(null);
  const headRef = useRef(null);
  const flashRef = useRef(null);
  const progressRef = useRef(0);
  const idRef = useRef(null);
  if (idRef.current === null) idRef.current = `hold-ring-${++idCounter}`;
  const maskId = `${idRef.current}-mask`;

  useEffect(() => {
    const apply = (p) => {
      progressRef.current = p;
      if (sweepRef.current) {
        sweepRef.current.style.strokeDashoffset = String(100 - p * 100);
      }
      if (headRef.current) {
        // Dash of HEAD_LEN ending exactly at the sweep's leading edge.
        headRef.current.style.strokeDashoffset = String(HEAD_LEN - p * 100);
        headRef.current.style.opacity = p > 0 && p < 1 ? "1" : "0";
      }
    };
    const flash = flashRef.current;
    const from = active ? 0 : progressRef.current;
    const to = active ? 1 : 0;
    if (flash) flash.style.animation = "none";
    if (from === to) {
      apply(from);
      return undefined;
    }
    const duration = active ? durationMs : unwindMs;
    const start = performance.now();
    let raf;
    apply(from);
    const tick = (now) => {
      const t = Math.min((now - start) / duration, 1);
      apply(from + (to - from) * t);
      if (t < 1) {
        raf = requestAnimationFrame(tick);
      } else if (active && flashOnComplete && flash) {
        flash.style.animation = "holdRingFlash 420ms ease-out forwards";
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, durationMs, unwindMs, flashOnComplete]);

  const R = { rx: rx + gap, ry: ry + gap };
  // Progress ellipses: rx/ry swapped + rotated so the path starts at
  // 12 o'clock and runs clockwise.
  const clockwise = {
    cx,
    cy,
    rx: R.ry,
    ry: R.rx,
    transform: `rotate(-90 ${cx} ${cy})`,
    fill: "none",
    pathLength: 100,
  };
  const isTicks = variant === "ticks";
  const isComet = variant === "comet";
  const tickDash = `${TICK_ON} ${TICK_OFF}`;

  return (
    <svg
      width={width}
      height={height}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        pointerEvents: "none",
        zIndex,
        opacity: active ? 1 : 0,
        transition: `opacity ${active ? 160 : unwindMs + 80}ms ease-out`,
        ...style,
      }}
    >
      <style>{`
        @keyframes holdRingFlash {
          0%   { opacity: 0.9; stroke-width: ${thickness}px; }
          100% { opacity: 0;   stroke-width: ${thickness * 5}px; }
        }
      `}</style>

      {isTicks && (
        <defs>
          {/* The sweep, used as a mask so ticks light up as it passes. */}
          <mask id={maskId} maskUnits="userSpaceOnUse">
            <ellipse
              ref={sweepRef}
              {...clockwise}
              stroke="#fff"
              strokeWidth={thickness + 6}
              strokeDasharray={100}
              strokeDashoffset={100}
            />
          </mask>
        </defs>
      )}

      {/* Track */}
      <ellipse
        cx={cx}
        cy={cy}
        rx={R.rx}
        ry={R.ry}
        fill="none"
        stroke={trackColor}
        strokeWidth={isTicks ? thickness : Math.max(thickness - 0.5, 1)}
        {...(isTicks
          ? { pathLength: 100, strokeDasharray: tickDash, transform: `rotate(-90 ${cx} ${cy})`, rx: R.ry, ry: R.rx }
          : {})}
      />

      {isTicks ? (
        <ellipse
          {...clockwise}
          stroke={color}
          strokeWidth={thickness}
          strokeDasharray={tickDash}
          mask={`url(#${maskId})`}
          style={{ filter: "drop-shadow(0 0 2px rgba(255,255,255,0.8))" }}
        />
      ) : (
        <ellipse
          ref={sweepRef}
          {...clockwise}
          stroke={color}
          strokeWidth={thickness}
          strokeLinecap="round"
          strokeDasharray={100}
          strokeDashoffset={100}
          style={{
            opacity: isComet ? 0.55 : 1,
            filter: isComet ? undefined : "drop-shadow(0 0 2px rgba(255,255,255,0.8))",
          }}
        />
      )}

      {isComet && (
        <ellipse
          ref={headRef}
          {...clockwise}
          stroke={color}
          strokeWidth={thickness + 1}
          strokeLinecap="round"
          strokeDasharray={`${HEAD_LEN} ${100 - HEAD_LEN}`}
          strokeDashoffset={HEAD_LEN}
          style={{
            opacity: 0,
            filter:
              "drop-shadow(0 0 2px rgba(255,255,255,0.9)) drop-shadow(0 0 6px rgba(255,255,255,0.6))",
          }}
        />
      )}

      {/* Completion flash: widens and fades out once the ring closes. */}
      <ellipse
        ref={flashRef}
        cx={cx}
        cy={cy}
        rx={R.rx}
        ry={R.ry}
        fill="none"
        stroke={color}
        strokeWidth={thickness}
        style={{ opacity: 0 }}
      />
    </svg>
  );
}
